import React, { useEffect, useState } from "react";
import TeamHeader from "../TeamHeader/TeamHeader";
import "./RequestActivity.css";
import Chart from "react-apexcharts";
import LoadingSpinner from '../../backend/components/LoadingSpinner';


// Icons
import { IoMdArrowDropdown } from "react-icons/io";
import { IoOptionsOutline } from "react-icons/io5";
import { PiGearSix } from "react-icons/pi";

interface HttpRequestData {
    request_id: number;
    http_code: number;
    timestamp: string;
    status: boolean;
    url: string;
    description: string;
    customer_id: number;
}

interface MarketingOrg {
    id: number;
    ORG_NAME: string;
    ORG_LOGO: string;
    ORG_PLAN: string;
}

const RequestActivity = () => {

    const [timeRange, setTimeRange] = useState<"12m" | "1m" | "1w" | "24h">("12m");
    const MAX_TICKS = 8;
    const [loading, setLoading] = useState(true);


    // Scroll to top on page opening
    useEffect(() => window.scrollTo(0, 0), []);


    /* ----------------------------------------------------
        Fetching HTTP Request History and Orgs
    ---------------------------------------------------- */
    const [httpHistory, setHttpHistory] = useState<HttpRequestData[]>([]);
    const [orgs, setOrgs] = useState<MarketingOrg[]>([]);

    const fetchAllData = async () => {
        setLoading(true);

        try {
            const [httpRes, orgRes] = await Promise.all([
                fetch("/api/aiteam/http_request/history/", { credentials: "same-origin" }),
                fetch("/portal/api/marketing_orgs/", { credentials: "same-origin" })
            ]);

            if (!httpRes.ok) throw new Error(`HTTP fetch ${httpRes.status}`);
            if (!orgRes.ok) throw new Error(`ORG fetch HTTP ${orgRes.status}`);

            setHttpHistory(await httpRes.json());
            setOrgs(await orgRes.json());

        } catch (err) {
            console.error("❌ Error loading data:", err);
            setHttpHistory([]);
            setOrgs([]);
        }

        setLoading(false);
    };

    useEffect(() => {
        fetchAllData();
    }, []);


    /* ----------------------------------------------------
        DROPDOWN LOGIC
    ---------------------------------------------------- */

    // Filter state
    const [requestFilter, setRequestFilter] = useState<{ status?: string; customer?: string; http_code?: string }>({});

    // Dropdown open state
    const [requestDropdownOpen, setRequestDropdownOpen] = useState({
        status: false,
        customer: false,
        http_code: false, // new
    });

    // Close dropdowns when clicking outside
    useEffect(() => {
        function handleClickOutside(e: MouseEvent) {
            const target = e.target as HTMLElement;

            if (
                target.closest(".general-sort-options") ||
                target.closest(".general-sort-dropdown") ||
                target.closest(".general-dropdown") ||
                target.closest(".general-dropdown-options")
            ) {
                return;
            }

            setRequestDropdownOpen({
                status: false,
                customer: false,
                http_code: false,
            });

            document.querySelectorAll(".general-sort-dropdown").forEach((el) => {
                (el as HTMLDetailsElement).open = false;
            });
        }

        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, []);


    const toggleFilter = (setStateFn: any, key: string, value: string) => {
        setStateFn((prev: any) => ({
            ...prev,
            [key]: prev[key] === value ? undefined : value,
        }));
    };

    interface DropdownProps {
        title: string;
        options: string[];
        selected?: string;
        onSelect: (value: string) => void;
        open: boolean;
        setOpen: (val: boolean) => void;
    }

    const Dropdown: React.FC<DropdownProps> = ({ title, options, selected, onSelect, open, setOpen }) => {
        return (
            <div className="general-dropdown">
                <div className="general-dropdown-header" onClick={() => setOpen(!open)}>
                    <span>{title}</span>
                    <IoMdArrowDropdown className={`general-dropdown-arrow ${open ? "open" : ""}`} />
                </div>
                <div className={`general-dropdown-options ${open ? "open" : ""}`}>
                    {options.map((o) => (
                        <div
                            key={o}
                            className={`general-dropdown-option ${selected === o ? "active" : ""}`}
                            onClick={() => onSelect(o)}
                        >
                            {o}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const isWithinTimeRange = (timestamp: string, range: string) => {
        const now = new Date();
        const t = new Date(timestamp);

        switch (range) {
            case "12m": {
                const yearAgo = new Date();
                yearAgo.setUTCMonth(now.getUTCMonth() - 12);
                return t >= yearAgo;
            }

            case "1m": {
                const monthAgo = new Date();
                monthAgo.setUTCMonth(now.getUTCMonth() - 1);
                return t >= monthAgo;
            }

            case "1w": {
                const weekAgo = new Date();
                weekAgo.setUTCDate(now.getUTCDate() - 7);
                return t >= weekAgo;
            }

            case "24h": {
                const dayAgo = new Date();
                dayAgo.setUTCHours(now.getUTCHours() - 24);
                return t >= dayAgo;
            }

            default:
                return true;
        }
    };


    const filteredHttpHistory = React.useMemo(() => {
        return httpHistory.filter(h => {
            // FIRST: filter by time range (affects both graph + table)
            if (!isWithinTimeRange(h.timestamp, timeRange)) return false;

            // Filter by status
            if (requestFilter.status) {
                const statusText = h.status ? "Successful" : "Failed";
                if (statusText !== requestFilter.status) return false;
            }

            // Filter by customer using orgs
            if (requestFilter.customer) {
                const orgName = orgs.find(o => o.id === h.customer_id)?.ORG_NAME;
                if (orgName !== requestFilter.customer) return false;
            }

            // Filter by HTTP code
            if (requestFilter.http_code) {
                if (h.http_code.toString() !== requestFilter.http_code) return false;
            }

            return true;
        });
    }, [httpHistory, requestFilter, orgs, timeRange]);





    /* ----------------------------------------------------
        Organizing HTTP Request History to plot
    ---------------------------------------------------- */

    // Last 12 months, past to now
    const monthlyStats = React.useMemo(() => {
        const stats: Record<string, Record<number, number>> = {};

        // Build last 12 months labels (YYYY-M)
        const now = new Date();
        const months: string[] = [];
        for (let i = 11; i >= 0; i--) {
            const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
            const label = `${d.getUTCFullYear()}-${d.getUTCMonth() + 1}`;
            months.push(label);
            stats[label] = {};
        }

        // Count requests into months
        filteredHttpHistory.forEach(item => {
            const d = new Date(item.timestamp);
            const label = `${d.getUTCFullYear()}-${d.getUTCMonth() + 1}`;
            if (stats[label]) { // only count last 12 months
                if (!stats[label][item.http_code]) stats[label][item.http_code] = 0;
                stats[label][item.http_code] += 1;
            }
        });

        return stats;
    }, [filteredHttpHistory]);

    // Unique sorted 12 months
    const months = React.useMemo(() => {
        return Object.keys(monthlyStats);
    }, [monthlyStats]);

    // Unique status codes (200, 400, 404...)
    const statusCodes = React.useMemo(() => {
        const set = new Set<number>();
        filteredHttpHistory.forEach(h => set.add(h.http_code));
        return Array.from(set).sort();
    }, [filteredHttpHistory]);

    // Build ApexCharts series
    const graphSeries = React.useMemo(() => {
        return statusCodes.map(code => ({
            name: `Request ${code}`,
            data: months.map(m => monthlyStats[m]?.[code] || 0),
        }));
    }, [months, statusCodes, monthlyStats]);

    // Last 30 days
    const last30DaysData = React.useMemo(() => {
        const days: string[] = [];
        const stats: Record<string, Record<number, number>> = {};

        for (let i = 29; i >= 0; i--) {
            const d = new Date();
            d.setUTCDate(d.getUTCDate() - i);
            d.setUTCHours(0, 0, 0, 0);

            const label = d.toLocaleString("en-US", { month: "short", day: "numeric", timeZone: "UTC" }); // "Jan 12"
            days.push(label);
            stats[label] = {};
        }

        filteredHttpHistory.forEach(h => {
            const d = new Date(h.timestamp);
            d.setUTCHours(0, 0, 0, 0);

            const label = d.toLocaleString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

            if (stats[label]) {
                if (!stats[label][h.http_code]) stats[label][h.http_code] = 0;
                stats[label][h.http_code] += 1;
            }
        });

        const series = statusCodes.map(code => ({
            name: `Request ${code}`,
            data: days.map(day => stats[day]?.[code] || 0),
        }));

        return { days, series };
    }, [filteredHttpHistory, statusCodes]);

    // 1 week
    const weekData = React.useMemo(() => {
        const days: string[] = [];
        const stats: Record<string, Record<number, number>> = {};

        for (let i = 6; i >= 0; i--) {
            const d = new Date();
            d.setUTCDate(d.getUTCDate() - i);
            d.setUTCHours(0, 0, 0, 0);

            const label = d.toLocaleString("en-US", { month: "short", day: "numeric", timeZone: "UTC" }); // "Jan 12"
            days.push(label);
            stats[label] = {};
        }

        filteredHttpHistory.forEach(h => {
            const d = new Date(h.timestamp);
            d.setUTCHours(0, 0, 0, 0);

            const label = d.toLocaleString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

            if (stats[label]) {
                if (!stats[label][h.http_code]) stats[label][h.http_code] = 0;
                stats[label][h.http_code] += 1;
            }
        });

        const series = statusCodes.map(code => ({
            name: `Request ${code}`,
            data: days.map(day => stats[day]?.[code] || 0)
        }));

        return { days, series };
    }, [filteredHttpHistory, statusCodes]);

    // Last 24 hours
    const last24HoursData = React.useMemo(() => {
        const hours: string[] = [];
        const stats: Record<string, Record<number, number>> = {};

        for (let i = 23; i >= 0; i--) {
            const d = new Date();
            d.setUTCHours(d.getUTCHours() - i, 0, 0, 0);

            const label = d.toLocaleString("en-US", {
                hour: "numeric",
                hour12: true,
                timeZone: "UTC"
            }); // "2 PM"

            hours.push(label);
            stats[label] = {};
        }

        filteredHttpHistory.forEach(h => {
            const d = new Date(h.timestamp);
            d.setUTCMinutes(0, 0, 0); // normalize to hour

            const label = d.toLocaleString("en-US", {
                hour: "numeric",
                hour12: true,
                timeZone: "UTC"
            });

            if (stats[label]) {
                if (!stats[label][h.http_code]) stats[label][h.http_code] = 0;
                stats[label][h.http_code] += 1;
            }
        });

        const series = statusCodes.map(code => ({
            name: `Request ${code}`,
            data: hours.map(hLabel => stats[hLabel]?.[code] || 0)
        }));

        return { hours, series };
    }, [filteredHttpHistory, statusCodes]);


    // Chart Data based on time filter
    const chartData = React.useMemo(() => {
        switch (timeRange) {
            case "1m":
                return {
                    categories: last30DaysData.days,
                    series: last30DaysData.series
                };
            case "1w":
                return {
                    categories: weekData.days,
                    series: weekData.series
                };
            case "24h":
                return {
                    categories: last24HoursData.hours,
                    series: last24HoursData.series
                };
            default:
                return {
                    categories: months,
                    series: graphSeries
                };
        }
    }, [timeRange, last30DaysData, weekData, last24HoursData, months, graphSeries]);


    /* ----------------------------------------------------
        Helper Color pallets for the different https
    ---------------------------------------------------- */

    const STATUS_SHADES: Record<number, string[]> = {
        1: ["#1976D2", "#1E88E5", "#42A5F5"],        // 1xx
        2: ["#2E7D32", "#43A047", "#66BB6A"],        // 2xx
        3: ["#F9A825", "#FBC02D", "#FDD835"],        // 3xx
        4: ["#C62828", "#D32F2F", "#EF5350"],        // 4xx
        5: ["#6A1B9A", "#8E24AA", "#AB47BC"],        // 5xx
    };

    // Pick a shade inside the group based on modulus (keeps consistent but unique)
    const getColorForCode = (code: number): string => {
        const group = Math.floor(code / 100);
        const shades = STATUS_SHADES[group];
        if (!shades) return "#9E9E9E"; // fallback gray

        return shades[code % shades.length];
    };

    // Helper: truncate URL
    const shortUrl = (url: string) =>
        url.length > 15 ? `${url.slice(0, 15)}...` : url;

    /* ----------------------------------------------------
        Handling ACTIONS
    ---------------------------------------------------- */
    const exportToCSV = (filename: string, rows: any[]) => {
        if (!rows || rows.length === 0) {
            alert("No data available for export.");
            return;
        }

        // Extract headers dynamically
        const headers = Object.keys(rows[0]);

        // Escape values properly
        const escape = (value: any) => {
            if (value === null || value === undefined) return "";
            const v = value.toString().replace(/"/g, '""');
            return `"${v}"`;
        };

        const csvContent = [
            headers.join(","),                                   // header line
            ...rows.map(row => headers.map(h => escape(row[h])).join(",")) // rows
        ].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
    };

    return (

        loading ? (
            <div className="customer-directory-body">
                <TeamHeader title="AI" />
                <h1 className="cutomer-title">Request Activity</h1>
                <div style={{ width: '100%', display: 'flex', justifyContent: 'center', height:'80vh' }}>
                    <LoadingSpinner />
                </div>
            </div>
        ) : (

            <div className="customer-directory-body">
                <TeamHeader title="AI" />

                <h1 className="cutomer-title">Request Activity</h1>

                <div className="ai-blocks">

                    <div className="ai-block request-ext">
                        <div className="ai-action-buttons">
                            {/* ---------------- FILTER ---------------- */}
                            <details className="general-sort-dropdown">
                                <summary className="general-sort-summary">
                                    <IoOptionsOutline style={{ marginRight: "0.25rem", fontSize: "1rem" }} />
                                    <span>Filter</span>
                                </summary>
                                <div className="general-sort-options">

                                    {/* HTTP Code Filter */}
                                    <Dropdown
                                        title="Request"
                                        options={Array.from(new Set(httpHistory.map(h => h.http_code.toString()))).sort()} // unique codes
                                        selected={requestFilter.http_code}
                                        onSelect={(code) => toggleFilter(setRequestFilter, "http_code", code)}
                                        open={requestDropdownOpen.http_code ?? false}
                                        setOpen={(val) => setRequestDropdownOpen(prev => ({ ...prev, http_code: val }))}
                                    />

                                    {/* Customer Filter */}
                                    <Dropdown
                                        title="Customer"
                                        options={orgs.map(o => o.ORG_NAME)} // use ORG_NAME for display
                                        selected={requestFilter.customer}
                                        onSelect={(customer) => toggleFilter(setRequestFilter, "customer", customer)}
                                        open={requestDropdownOpen.customer}
                                        setOpen={(val) => setRequestDropdownOpen(prev => ({ ...prev, customer: val }))}
                                    />

                                    {/* Status Filter */}
                                    <Dropdown
                                        title="Status"
                                        options={["Successful", "Failed"]}
                                        selected={requestFilter.status}
                                        onSelect={(status) => toggleFilter(setRequestFilter, "status", status)}
                                        open={requestDropdownOpen.status}
                                        setOpen={(val) => setRequestDropdownOpen(prev => ({ ...prev, status: val }))}
                                    />
                                    <button onClick={() => setRequestFilter({})}>Reset</button>
                                </div>
                            </details>


                        </div>

                        <div className="request-title-and-times">
                            <h1>HTTP Requests</h1>
                            <div className="request-times">
                                <button
                                    className={timeRange === "12m" ? "active" : ""}
                                    onClick={() => setTimeRange("12m")}
                                >
                                    12 months
                                </button>
                                <button
                                    className={timeRange === "1m" ? "active" : ""}
                                    onClick={() => setTimeRange("1m")}
                                >
                                    1 month
                                </button>
                                <button
                                    className={timeRange === "1w" ? "active" : ""}
                                    onClick={() => setTimeRange("1w")}
                                >
                                    1 week
                                </button>
                                <button
                                    className={timeRange === "24h" ? "active" : ""}
                                    onClick={() => setTimeRange("24h")}
                                >
                                    24 hours
                                </button>
                            </div>
                        </div>


                        <div className="request-graph-container">
                            <Chart
                                type="line"
                                height="100%"
                                series={chartData.series}
                                options={{
                                    chart: { toolbar: { show: false }, zoom: { enabled: false } },
                                    stroke: { curve: "smooth", width: 3 },
                                    markers: { size: 5, strokeWidth: 2, hover: { size: 7 } },
                                    xaxis: {
                                        categories: chartData.categories,
                                        tickAmount: Math.min(chartData.categories.length, MAX_TICKS)
                                    },
                                    yaxis: { min: 0, tickAmount: 5 },
                                    legend: {
                                        position: "top",
                                        horizontalAlign: "center",
                                        markers: { size: 6, shape: "circle", strokeWidth: 0 }
                                    },
                                    grid: { borderColor: "#e0e0e0" },
                                    colors: statusCodes.map(code => getColorForCode(code)),
                                }}
                            />



                        </div>

                    </div>

                    <div className="ai-block request-ext">
                        <div className="ai-action-buttons ai-request-action-container">
                            <details className="general-sort-dropdown">
                                <summary className="general-sort-summary">
                                    <PiGearSix style={{ marginRight: "0.25rem", fontSize: "1rem" }} />
                                    <span>Actions</span>
                                </summary>

                                <div className="general-sort-options">
                                    <div
                                        className="general-sort-option"
                                        onClick={() => exportToCSV("http_request.csv", filteredHttpHistory)}
                                    >
                                        Export CSV
                                    </div>

                                </div>
                            </details>
                        </div>

                        <div className="request-title-and-times">
                            <h1>HTTP Requests Details</h1>
                        </div>

                        <div className="request-table">
                            <div className="request-table-header">
                                <div>Request</div>
                                <div>Date (UTC)</div>
                                <div>Status</div>
                                <div>URL</div>
                                <div>Customer</div>
                                <div>Description</div>
                            </div>

                            <div className="request-table-body">
                                {filteredHttpHistory.map(req => {
                                    const date = new Date(req.timestamp);
                                    const formatted = date.toLocaleString("en-US", {
                                        year: "numeric",
                                        month: "short",
                                        day: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        timeZone: "UTC"
                                    });

                                    return (
                                        <div key={req.request_id} className="request-table-row">
                                            <div
                                                className="request-code"
                                                style={{ color: getColorForCode(req.http_code) }}
                                            >
                                                {req.http_code}
                                            </div>

                                            <div>{formatted}</div>

                                            <td>
                                                <div className={`request-status ${req.status ? "success" : "failed"}`}>
                                                    {req.status ? "Successful" : "Failed"}
                                                </div>
                                            </td>

                                            <div>
                                                <a href={req.url} target="_blank" rel="noreferrer">{shortUrl(req.url)}</a>
                                            </div>

                                            <div>
                                                {orgs.find(o => o.id === req.customer_id)?.ORG_NAME || "N/A"}
                                            </div>


                                            <div>{req.description || "-"}</div>
                                        </div>
                                    );
                                })}
                            </div>


                        </div>

                    </div>

                </div>
            </div>

        )
    );
}

export default RequestActivity